/**
 * Device Registration Hook
 *
 * Registers the current device with the backend API when a user signs in.
 * This is for subscription abuse detection (observability only, no enforcement).
 *
 * The hook:
 * 1. Gets device info from the Electron main process (fingerprint, name, platform)
 * 2. Registers/updates the device via the backend API when user authenticates
 * 3. Runs silently in the background - device registration is not critical
 */

import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { registerDevice, type DeviceInfo } from '../services/deviceService';

/**
 * Get device info from the Electron main process.
 * Returns null if not running in Electron or on error.
 */
async function getDeviceInfoFromElectron(): Promise<DeviceInfo | null> {
  try {
    // Check if running in Electron
    if (typeof window === 'undefined' || !window.electronAPI?.device) {
      return null;
    }

    const info = await window.electronAPI.device.getInfo();
    return {
      fingerprint: info.fingerprint,
      deviceName: info.deviceName,
      platform: info.platform,
    };
  } catch (error) {
    console.error('[useDeviceRegistration] Failed to get device info:', error);
    return null;
  }
}

/**
 * Hook that registers the current device when user signs in.
 *
 * Usage: Call this hook once at the app root level.
 *
 * @example
 * function App() {
 *   useDeviceRegistration();
 *   return <YourAppContent />;
 * }
 */
export function useDeviceRegistration(): void {
  const { user, isLoading } = useAuth();
  const lastRegisteredUserId = useRef<string | null>(null);

  useEffect(() => {
    // Skip while auth is loading
    if (isLoading) {
      return;
    }

    // Skip if no user is signed in
    if (!user) {
      // Reset tracking when user signs out
      lastRegisteredUserId.current = null;
      return;
    }

    // Skip if we already registered for this user (prevents duplicate registrations)
    if (lastRegisteredUserId.current === user.id) {
      return;
    }

    // Mark as registered immediately to prevent concurrent duplicate calls.
    // The effect can fire many times before the async registration completes.
    lastRegisteredUserId.current = user.id;

    // Register device in background (don't block or show errors to user)
    const registerInBackground = async () => {
      try {
        const deviceInfo = await getDeviceInfoFromElectron();
        if (!deviceInfo) {
          // Not running in Electron or device info unavailable - skip silently
          return;
        }

        await registerDevice(deviceInfo);
        console.log('[useDeviceRegistration] Device registered for user:', user.email);
      } catch (error) {
        // Silent failure - device registration is not critical
        console.error('[useDeviceRegistration] Registration failed:', error);
        // Reset so a future render can retry
        lastRegisteredUserId.current = null;
      }
    };

    registerInBackground();
  }, [user, isLoading]);
}
